import React, { useState, useEffect, useCallback } from 'react';
import { useStory } from '../../context/StoryContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { modifyImagePrompt } from '../../services/aiService';
import { useAuth } from '../../context/AuthContext';

const ImageGallery = () => {
  const { user } = useAuth();
  const { state, updateGalleryImage, deleteGalleryImage, addToGallery } = useStory();
  const { gallery, character, ai } = state;
  const [selectedImage, setSelectedImage] = useState(null);
  const [modificationRequest, setModificationRequest] = useState('');
  const [isModifying, setIsModifying] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');

  const hasApiKey = !!((import.meta.env.VITE_OPENROUTER_API_KEY && import.meta.env.VITE_OPENROUTER_API_KEY !== 'dummy_key_for_testing') || (ai.apiKey && ai.apiKey.trim().length > 0));

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Full resolution image for display
        const fullCanvas = document.createElement('canvas');
        fullCanvas.width = img.width;
        fullCanvas.height = img.height;
        const fullCtx = fullCanvas.getContext('2d');
        fullCtx.drawImage(img, 0, 0);
        const fullBase64 = fullCanvas.toDataURL('image/jpeg', 0.9);

        // Compress image using canvas for AI reference (Pollinations URL limit workaround)
        const canvas = document.createElement('canvas');
        const MAX_SIZE = 150;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // High compression JPEG
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
        const timestamp = new Date().toISOString();

        const newImage = {
          id: Date.now(),
          title: 'Untitled Image',
          // Use the full resolution image for the URL to display.
          url: fullBase64,
          compressedBase64: compressedBase64, // Kept for AI reference prompt compatibility
          timestamp,
          baseScene: `User uploaded artwork on ${new Date(timestamp).toLocaleString()}`,
          modifications: 0,
          history: [],
        };
        addToGallery(newImage);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);

    // Reset input value to allow uploading the same file or a second file correctly
    e.target.value = null;
  };

  const handleDownload = async (url, timestamp) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `weaver-scene-${new Date(timestamp).getTime()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Failed to download image', err);
      // Fallback for direct download
      window.open(url, '_blank');
    }
  };

  const currentIndex = selectedImage ? gallery.findIndex(img => img.id === selectedImage.id) : -1;

  const handlePrevImage = useCallback(() => {
    if (currentIndex > 0) {
      setSelectedImage(gallery[currentIndex - 1]);
    }
  }, [currentIndex, gallery]);

  const handleNextImage = useCallback(() => {
    if (currentIndex < gallery.length - 1) {
      setSelectedImage(gallery[currentIndex + 1]);
    }
  }, [currentIndex, gallery]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'ArrowLeft') {
        handlePrevImage();
      } else if (e.key === 'ArrowRight') {
        handleNextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, handlePrevImage, handleNextImage]);

  const emptyState = (!gallery || gallery.length === 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-serif font-bold text-gray-900 dark:text-white">Gallery</h3>
        <label className="cursor-pointer bg-gray-100 hover:bg-purple-200 dark:bg-purple-900/50 dark:hover:bg-purple-800 text-gray-900 dark:text-purple-300 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-colors flex items-center space-x-2">
          <SafeIcon icon={FiIcons.FiUploadCloud} />
          <span>Upload Art</span>
          <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
        </label>
      </div>

      {emptyState ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-900 dark:text-purple-400 space-y-4">
          <SafeIcon icon={FiIcons.FiImage} className="text-4xl opacity-50" />
          <p className="text-xs uppercase tracking-widest font-black text-center">Gallery Empty</p>
          <p className="text-[10px] text-center max-w-[200px] leading-relaxed">
            Enable Scene Generation in your AI settings, or upload your own artwork to fill this gallery.
          </p>
        </div>
      ) : (
      <>
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-purple-800 pb-2">
          <h3 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest flex items-center space-x-2">
            <SafeIcon icon={FiIcons.FiImage} />
            <span>Visual Archive</span>
          </h3>
          <span className="text-[10px] font-bold text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
            {gallery.length} Images
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {gallery.map((img) => (
            <div
              key={img.id}
              onClick={() => setSelectedImage(img)}
              className="relative group rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            >
              <img
                src={img.url}
                alt="Scene Visualization"
                className="w-full h-32 object-cover border border-gray-100 dark:border-purple-800 bg-gray-100 dark:bg-purple-900"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://placehold.co/400x400/2d1b4e/a78bfa?text=Image+Lost";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                <span className="text-[8px] text-white font-mono">
                  {img.timestamp ? new Date(img.timestamp).toLocaleDateString() : 'Unknown Date'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </>
      )}

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm overflow-y-auto p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="min-h-full flex items-center justify-center py-8">
              <div
                className="relative max-w-5xl w-full flex flex-col items-center"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute -top-10 right-0 text-white hover:text-amber-500 p-2 z-50"
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-3xl" />
                </button>

                <div className="w-full flex justify-center mb-4 min-h-[2rem]">
                  {isEditingTitle ? (
                    <input
                      type="text"
                      autoFocus
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      onBlur={() => {
                        updateGalleryImage(selectedImage.id, { title: editedTitle });
                        setSelectedImage(prev => ({ ...prev, title: editedTitle }));
                        setIsEditingTitle(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          updateGalleryImage(selectedImage.id, { title: editedTitle });
                          setSelectedImage(prev => ({ ...prev, title: editedTitle }));
                          setIsEditingTitle(false);
                        }
                      }}
                      className="bg-transparent border-b border-amber-500 text-white text-xl font-serif text-center focus:outline-none"
                    />
                  ) : (
                    <div className="flex items-center space-x-2 group">
                      <h2 className="text-white text-xl font-serif font-bold text-center">
                        {selectedImage.title || 'Untitled Image'}
                      </h2>
                      {(selectedImage.user_id === user?.id || state.ownerId === user?.id) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditedTitle(selectedImage.title || 'Untitled Image');
                            setIsEditingTitle(true);
                          }}
                          className="text-white/50 hover:text-amber-500 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <SafeIcon icon={FiIcons.FiEdit2} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="relative max-w-full flex justify-center items-center w-full group/image">
                  {currentIndex > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                      className="absolute left-2 md:-left-12 text-white bg-black/50 hover:bg-black/80 hover:text-amber-500 p-2 md:p-3 rounded-full z-50 transition-colors opacity-0 group-hover/image:opacity-100 focus:opacity-100 shadow-xl backdrop-blur-sm"
                      title="Previous Image"
                    >
                      <SafeIcon icon={FiIcons.FiChevronLeft} className="text-3xl" />
                    </button>
                  )}

                  <img
                    src={selectedImage.url}
                    alt="Enlarged Scene Visualization"
                    className={`w-full h-full max-h-[85vh] min-h-[50vh] md:min-h-[70vh] object-contain rounded-lg shadow-2xl border border-white/20 transition-opacity duration-300 ${isModifying ? 'opacity-30 blur-sm' : 'opacity-100'}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://placehold.co/1024x1024/2d1b4e/a78bfa?text=Image+Lost";
                    }}
                  />

                  {currentIndex >= 0 && currentIndex < gallery.length - 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                      className="absolute right-2 md:-right-12 text-white bg-black/50 hover:bg-black/80 hover:text-amber-500 p-2 md:p-3 rounded-full z-50 transition-colors opacity-0 group-hover/image:opacity-100 focus:opacity-100 shadow-xl backdrop-blur-sm"
                      title="Next Image"
                    >
                      <SafeIcon icon={FiIcons.FiChevronRight} className="text-3xl" />
                    </button>
                  )}

                {isModifying && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-amber-400 space-y-4">
                    <SafeIcon icon={FiIcons.FiImage} className="text-5xl animate-pulse" />
                    <p className="text-sm font-black tracking-widest uppercase animate-pulse drop-shadow-md">
                      Visualizing Changes...
                    </p>
                  </div>
                )}
              </div>

              {selectedImage.baseScene && (
                <div className="mt-4 w-full max-w-2xl bg-purple-900/60 p-4 rounded-xl border border-purple-500/30 backdrop-blur-md">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-amber-400 text-xs font-bold flex items-center space-x-2 uppercase tracking-widest">
                      <SafeIcon icon={FiIcons.FiEdit3} />
                      <span>Modify Image with AI</span>
                    </h4>

                    {selectedImage.history && selectedImage.history.length > 0 && (
                      <button
                        onClick={() => {
                          const previousState = selectedImage.history[selectedImage.history.length - 1];
                          const newHistory = selectedImage.history.slice(0, -1);
                          const revertedData = {
                            url: previousState.url,
                            baseScene: previousState.baseScene,
                            history: newHistory,
                            modifications: Math.max(0, (selectedImage.modifications || 1) - 1)
                          };
                          updateGalleryImage(selectedImage.id, revertedData);
                          setSelectedImage(prev => ({ ...prev, ...revertedData }));
                        }}
                        className="text-xs flex items-center space-x-1 text-gray-900 hover:text-amber-400 transition-colors bg-purple-800/50 px-2 py-1 rounded border border-purple-500/30 dark:text-purple-300"
                        title="Undo last modification"
                        disabled={isModifying}
                      >
                        <SafeIcon icon={FiIcons.FiRotateCcw} />
                        <span>Undo</span>
                      </button>
                    )}
                  </div>

                  {state.subscriptionTier === 'Wanderer' ? (
                    <div className="text-amber-400 text-sm p-2 bg-amber-900/20 rounded-md border border-amber-500/20 flex items-center space-x-2">
                      <SafeIcon icon={FiIcons.FiLock} />
                      <span>Priority Image Generation is available on the Storyteller tier and above. Please upgrade to modify images.</span>
                    </div>
                  ) : (!hasApiKey && selectedImage.modifications >= 2) ? (
                    <div className="text-red-400 text-sm p-2 bg-red-900/20 rounded-md border border-red-500/20 flex items-center space-x-2">
                      <SafeIcon icon={FiIcons.FiAlertCircle} />
                      <span>Modification limit reached. Use your own API key in settings to unlock unlimited modifications.</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-col">
                        <textarea
                          value={modificationRequest}
                          onChange={(e) => setModificationRequest(e.target.value)}
                          placeholder="What would you like to change? (e.g., 'Make it raining', 'Change her hair to red')"
                          className="w-full bg-purple-950/50 text-white border border-purple-700 rounded-md p-2 text-sm focus:outline-none focus:border-amber-500"
                          rows={2}
                          disabled={isModifying}
                        />
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-xs text-gray-900 opacity-70 dark:text-purple-300">
                            Suggestions: "Make it nighttime", "Add a dragon in the sky", "Watercolor style"
                          </span>
                          {!hasApiKey && (
                            <span className="text-xs font-mono text-amber-500">
                              {2 - (selectedImage.modifications || 0)} modifications left
                            </span>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={async () => {
                          if (!modificationRequest.trim() || isModifying) return;
                          setIsModifying(true);
                          try {
                            const newUrl = await modifyImagePrompt(
                              selectedImage.baseScene,
                              modificationRequest,
                              character,
                              ai.imageGenerator?.style || 'realistic',
                              ai.apiKey,
                              ai.model,
                              gallery
                            );
                            const updatedImageData = {
                              url: newUrl,
                              modifications: (selectedImage.modifications || 0) + 1,
                              baseScene: `${selectedImage.baseScene}. Modified: ${modificationRequest}`,
                              history: [...(selectedImage.history || []), { url: selectedImage.url, baseScene: selectedImage.baseScene }]
                            };
                            updateGalleryImage(selectedImage.id, updatedImageData);
                            setSelectedImage(prev => ({ ...prev, ...updatedImageData }));
                            setModificationRequest('');
                          } catch (err) {
                            console.error('Failed to modify image', err);
                          } finally {
                            setIsModifying(false);
                          }
                        }}
                        disabled={!modificationRequest.trim() || isModifying}
                        className="w-full bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:hover:bg-amber-600 text-white py-2 rounded-md font-bold transition-colors flex items-center justify-center space-x-2"
                      >
                        {isModifying ? (
                          <>
                            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" />
                            <span>Modifying...</span>
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiIcons.FiZap} />
                            <span>Modify Image</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

                <div className="mt-4 flex items-center justify-between w-full max-w-2xl text-white">
                  <span className="text-sm font-mono opacity-70">
                    {selectedImage.timestamp ? new Date(selectedImage.timestamp).toLocaleString() : 'Unknown Date'}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this image?')) {
                          deleteGalleryImage(selectedImage.id);
                          setSelectedImage(null);
                        }
                      }}
                      className="flex items-center space-x-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-full font-bold transition-colors"
                      title="Delete Image"
                    >
                      <SafeIcon icon={FiIcons.FiTrash2} />
                      <span className="hidden sm:inline">Delete</span>
                    </button>
                    <button
                      onClick={() => handleDownload(selectedImage.url, selectedImage.timestamp)}
                      className="flex items-center space-x-2 bg-purple-600 hover:bg-gray-500 text-white px-6 py-2 rounded-full font-bold transition-colors"
                      title="Download Image"
                    >
                      <SafeIcon icon={FiIcons.FiDownload} />
                      <span className="hidden sm:inline">Download</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImageGallery;